import { JobLineItem } from "../@types/job";
import { LineItemMaterial } from "../@types/expense";
import { isSpecimenLineItem } from "./lineItem";

/**
 * Computes the net change in inventory usage across all line items.
 *
 * Returns a map of inventoryItemUid -> quantity delta.
 * Positive = stock needs to be decremented (more material used).
 * Negative = stock needs to be incremented (material removed/reduced).
 */
export function computeMaterialDelta(
  oldLineItems: JobLineItem[],
  newLineItems: JobLineItem[]
): Map<string, number> {
  const oldTotals = sumMaterials(oldLineItems);
  const newTotals = sumMaterials(newLineItems);

  const delta = new Map<string, number>();

  // Items that increased or are new
  for (const [uid, qty] of newTotals) {
    const oldQty = oldTotals.get(uid) || 0;
    const diff = qty - oldQty;
    if (diff !== 0) delta.set(uid, diff);
  }

  // Items that were fully removed
  for (const [uid, qty] of oldTotals) {
    if (!newTotals.has(uid)) {
      delta.set(uid, -qty);
    }
  }

  return delta;
}

/**
 * Sums all material quantities across line items by inventoryItemUid.
 */
function sumMaterials(lineItems: JobLineItem[]): Map<string, number> {
  const totals = new Map<string, number>();

  for (const lineItem of lineItems) {
    const materials = lineItem.materials || [];
    for (const material of materials) {
      const current = totals.get(material.inventoryItemUid) || 0;
      totals.set(material.inventoryItemUid, current + material.quantity);
    }
  }

  return totals;
}

/**
 * Maps each inventoryItemUid to a label for each line item that uses it —
 * the mount ID (frontFacingId) for specimens, the service name for services —
 * so a material adjustment can show what it was for.
 */
export function mountIdsByInventoryItem(
  lineItems: JobLineItem[]
): Map<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const lineItem of lineItems) {
    const label = isSpecimenLineItem(lineItem)
      ? lineItem.frontFacingId
      : lineItem.service;
    if (!label) continue;
    for (const material of lineItem.materials || []) {
      if (!map.has(material.inventoryItemUid))
        map.set(material.inventoryItemUid, new Set());
      map.get(material.inventoryItemUid)!.add(label);
    }
  }
  return new Map(
    Array.from(map.entries()).map(([uid, set]) => [uid, Array.from(set)])
  );
}

/**
 * Returns all materials from all line items as a flat list,
 * useful for creating stock adjustments with references.
 */
export function flattenMaterials(
  lineItems: JobLineItem[]
): LineItemMaterial[] {
  const result: LineItemMaterial[] = [];
  for (const lineItem of lineItems) {
    result.push(...(lineItem.materials || []));
  }
  return result;
}
